import { FieldTag } from '../../modules/field/field/domain/field.interface';
import { multiStemDbh } from './multi-stem-dbh';
export const BS5837_MEAN_FIELD = 'dbh_mean';
const MIN_PLAUSIBLE_DBH_M = 0.001;
const MAX_PLAUSIBLE_DBH_M = 30;
export const isStemEligible = (set) => set.stems.length > 0 && set.aggregate !== undefined && set.trigger !== undefined;
export function resolveStemFieldSet(fields) {
    const stems = fields
        .filter(f => f.tag === FieldTag.MULTI_STEM_DBH)
        .slice()
        .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
    return {
        stems,
        aggregate: fields.find(f => f.tag === FieldTag.DBH),
        trigger: fields.find(f => f.tag === FieldTag.MULTI_STEM_TRIGGER),
        meanField: fields.find(f => f.tp2FieldName === BS5837_MEAN_FIELD),
    };
}
export function isMeanModeTree(liveAttributes, set) {
    if (!set.meanField)
        return false;
    const mean = liveAttributes[set.meanField.id];
    return mean !== null && mean !== undefined && mean !== '';
}
export const isStemEditorEngaged = (payload, set) => set.stems.some(stem => stem.id in payload);
export function usableStemValue(raw) {
    if (raw === null || raw === undefined || raw === '')
        return null;
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : null;
}
export const hasStemValues = (payload, set) => set.stems.some(stem => usableStemValue(payload[stem.id]) !== null);
export function completeStemValues(payload, set, liveAttributes = {}) {
    return set.stems.map(stem => usableStemValue(stem.id in payload ? payload[stem.id] : liveAttributes[stem.id]));
}
export const triggerPidForStemCount = (count) => (count > 1 ? 2 : 1);
export const isPlausibleDbhMetres = (value) => Number.isFinite(value) && value >= MIN_PLAUSIBLE_DBH_M && value <= MAX_PLAUSIBLE_DBH_M;
export const shouldBoundCheck = (lengthMeasurementUnit) => lengthMeasurementUnit !== null && lengthMeasurementUnit !== undefined;
export function dropStemFields(payload, set) {
    const ids = new Set([...set.stems, set.aggregate, set.trigger]
        .filter((f) => f !== undefined)
        .map(f => f.id));
    const dropped = Object.keys(payload).filter(id => ids.has(id));
    if (dropped.length === 0)
        return { payload, dropped };
    const next = { ...payload };
    for (const id of dropped)
        delete next[id];
    return { payload: next, dropped };
}
const unchanged = (attributes) => ({
    attributes,
    skipped: false,
    dropped: [],
});
export function applyStemWriteRules(attributes, fields, liveAttributes, engagement) {
    const set = resolveStemFieldSet(fields);
    if (!isStemEligible(set))
        return unchanged({ ...attributes });
    if (isMeanModeTree(liveAttributes, set)) {
        const { payload, dropped } = dropStemFields({ ...attributes }, set);
        return dropped.length > 0
            ? { attributes: payload, skipped: true, reason: 'bs5837-mean-mode', dropped }
            : unchanged(payload);
    }
    const payload = { ...attributes };
    const discarded = [];
    if (engagement === 'stem-values') {
        set.stems.forEach(stem => {
            if (stem.id in payload && usableStemValue(payload[stem.id]) === null) {
                delete payload[stem.id];
                discarded.push(stem.id);
            }
        });
    }
    const engaged = engagement === 'stem-values' ? hasStemValues(payload, set) : isStemEditorEngaged(payload, set);
    if (!engaged)
        return { attributes: payload, skipped: false, dropped: discarded };
    const values = completeStemValues(payload, set, liveAttributes);
    const present = values.filter((v) => v !== null);
    const aggregate = multiStemDbh(present);
    if (shouldBoundCheck(set.aggregate?.lengthMeasurementUnit) && present.length > 0) {
        if (!isPlausibleDbhMetres(aggregate)) {
            const { payload: bounded, dropped } = dropStemFields({ ...payload }, set);
            return {
                attributes: bounded,
                skipped: true,
                reason: `implausible aggregate DBH ${aggregate} — refusing the write`,
                dropped: [...discarded, ...dropped],
            };
        }
    }
    const next = { ...payload };
    set.stems.forEach((stem, i) => {
        next[stem.id] = values[i];
    });
    if (set.aggregate)
        next[set.aggregate.id] = present.length > 0 ? aggregate : null;
    return {
        attributes: next,
        skipped: false,
        dropped: discarded,
        ...(set.trigger
            ? { triggerPid: triggerPidForStemCount(present.length), triggerFieldId: set.trigger.id }
            : {}),
    };
}
//# sourceMappingURL=multi-stem-write.js.map